<template>
  <div>
    <!-- Hero -->
    <section class="hero mx-auto max-w-6xl px-4 pb-14 pt-12 sm:pb-20 sm:pt-16" aria-labelledby="hero-heading">
      <div class="grid items-center gap-10 lg:grid-cols-[1.15fr_0.85fr]">
        <div>
          <p class="mb-3 inline-flex items-center gap-2 rounded-full border border-[var(--surface-border)] bg-white/[0.03] px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--accent)]">
            <span class="h-1.5 w-1.5 rounded-full bg-[var(--accent)] shadow-[0_0_10px_var(--accent-glow)]" />
            Indie studio · games &amp; apps
          </p>
          <h1
            id="hero-heading"
            class="font-display max-w-xl text-4xl font-bold tracking-tight sm:text-5xl lg:text-[3.25rem] lg:leading-[1.08]"
          >
            Balcony Games — indie browser games, apps, and Chrome extensions
          </h1>
          <p class="mt-5 max-w-xl text-base text-[var(--text-muted)] sm:text-lg">
            Balcony Games ships browser &amp; mobile games, language tools, and everyday utilities —
            on the App Store, Google Play, and the open web.
          </p>
          <div class="mt-8 flex flex-wrap gap-3">
            <a class="btn-primary" href="#games">Explore games</a>
            <a class="btn-ghost" href="#contact">Get in touch</a>
          </div>
          <dl class="mt-10 grid max-w-md grid-cols-3 gap-3 text-center sm:text-left">
            <div class="stat">
              <dt class="text-[0.65rem] font-semibold uppercase tracking-wider text-[var(--text-muted)]">Games</dt>
              <dd class="font-display mt-0.5 text-2xl font-bold tabular-nums">{{ counts.games }}</dd>
            </div>
            <div class="stat">
              <dt class="text-[0.65rem] font-semibold uppercase tracking-wider text-[var(--text-muted)]">Apps</dt>
              <dd class="font-display mt-0.5 text-2xl font-bold tabular-nums">{{ counts.apps }}</dd>
            </div>
            <div class="stat">
              <dt class="text-[0.65rem] font-semibold uppercase tracking-wider text-[var(--text-muted)]">Extensions</dt>
              <dd class="font-display mt-0.5 text-2xl font-bold tabular-nums">{{ counts.extensions }}</dd>
            </div>
          </dl>
        </div>

        <!-- Featured strip -->
        <div class="featured-panel" aria-label="Featured titles">
          <p class="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-[var(--text-muted)]">
            Featured
          </p>
          <ul class="flex list-none flex-col gap-2.5 p-0">
            <li v-for="item in featured" :key="item.id">
              <a
                :href="catalogPath(item)"
                class="featured-row"
              >
                <img
                  v-if="item.image"
                  :src="item.image"
                  alt=""
                  width="48"
                  height="48"
                  class="h-12 w-12 shrink-0 rounded-xl object-cover ring-1 ring-white/10"
                  loading="lazy"
                />
                <span class="min-w-0 flex-1">
                  <span class="block truncate font-semibold text-[var(--text)]">{{ item.title }}</span>
                  <span class="mt-0.5 block truncate text-xs text-[var(--text-muted)]">{{
                    item.blurb
                  }}</span>
                </span>
                <span class="shrink-0 text-sm text-[var(--accent)]" aria-hidden="true">→</span>
              </a>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <!-- Jump chips -->
    <nav
      class="mx-auto max-w-6xl scroll-mt-20 px-4 pb-4"
      aria-label="Browse catalog"
    >
      <div class="flex flex-wrap gap-2">
        <a
          v-for="section in sections"
          :key="section.id"
          :href="`#${section.id}`"
          class="chip"
        >
          {{ section.title }}
          <span class="ml-1.5 tabular-nums opacity-60">{{ section.items.length }}</span>
        </a>
        <a href="#contact" class="chip">Contact</a>
      </div>
    </nav>

    <!-- Catalog sections -->
    <section
      v-for="section in sections"
      :id="section.id"
      :key="section.id"
      class="mx-auto max-w-6xl scroll-mt-24 px-4 py-12"
      :aria-labelledby="`${section.id}-heading`"
    >
      <header class="mb-7 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2
            :id="`${section.id}-heading`"
            class="font-display text-2xl font-bold tracking-tight sm:text-[1.75rem]"
          >
            {{ section.title }}
          </h2>
          <p class="mt-1.5 max-w-2xl text-sm text-[var(--text-muted)] sm:text-base">
            {{ section.description }}
          </p>
        </div>
      </header>

      <ul class="grid list-none gap-4 p-0 sm:grid-cols-2 lg:grid-cols-3">
        <li v-for="product in section.items" :key="product.id" class="product-card group">
          <article class="flex h-full flex-col p-4 sm:p-5">
            <a
              :href="catalogPath(product)"
              class="product-art mb-4 block overflow-hidden rounded-[0.85rem]"
              :aria-label="`${product.title} details`"
            >
              <img
                v-if="product.image"
                :src="product.image"
                :alt="`${product.title} icon`"
                width="256"
                height="256"
                class="aspect-square w-full object-cover transition duration-300 group-hover:scale-[1.04]"
                loading="lazy"
                decoding="async"
              />
            </a>
            <div class="flex items-start justify-between gap-2">
              <h3 class="text-[1.05rem] font-semibold leading-snug text-[var(--text)]">
                <a
                  :href="catalogPath(product)"
                  class="transition hover:text-[var(--accent)]"
                >
                  {{ product.title }}
                </a>
              </h3>
              <span
                v-if="product.status === 'coming_soon'"
                class="shrink-0 rounded-full bg-[var(--accent-soft)] px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-wide text-[var(--accent)]"
              >
                Soon
              </span>
            </div>
            <p v-if="product.description" class="mt-2 flex-1 text-sm leading-relaxed text-[var(--text-muted)]">
              {{ product.description }}
            </p>
            <ul
              v-if="linksFor(product).length"
              class="mt-4 flex list-none flex-wrap gap-1.5 p-0"
              :aria-label="`${product.title} store links`"
            >
              <li v-for="link in linksFor(product)" :key="link.platform">
                <a
                  :href="link.url"
                  class="chip"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {{ platformLabel(link.platform) }}
                </a>
              </li>
            </ul>
          </article>
        </li>
      </ul>
    </section>

    <!-- About + Contact -->
    <section class="border-t border-[var(--surface-border)] bg-[var(--bg-elevated)]/40">
      <div class="mx-auto grid max-w-6xl gap-12 px-4 py-16 lg:grid-cols-2 lg:gap-16">
        <div id="about" class="scroll-mt-24" aria-labelledby="about-heading">
          <h2 id="about-heading" class="font-display text-2xl font-bold tracking-tight">
            About Balcony Games
          </h2>
          <p class="mt-3 text-[var(--text-muted)]">
            We’re an indie studio shipping casual and arcade games, language-learning tools, and
            practical apps. Whether you want a quick browser session or a daily habit on mobile,
            we build for people who enjoy craft, curiosity, and play.
          </p>
          <ul class="mt-6 space-y-3 text-sm text-[var(--text-muted)]">
            <li class="flex gap-2">
              <span class="text-[var(--accent)]" aria-hidden="true">▸</span>
              <span>Games on web, App Store &amp; Google Play</span>
            </li>
            <li class="flex gap-2">
              <span class="text-[var(--accent)]" aria-hidden="true">▸</span>
              <span>Language vocabulary &amp; verb trainers</span>
            </li>
            <li class="flex gap-2">
              <span class="text-[var(--accent)]" aria-hidden="true">▸</span>
              <span
                >Email
                <a class="text-[var(--accent)] underline-offset-2 hover:underline" href="mailto:team@balconygames.com"
                  >team@balconygames.com</a
                >
                ·
                <a
                  class="text-[var(--accent)] underline-offset-2 hover:underline"
                  href="https://discord.gg/d6vJ4ERcsd"
                  target="_blank"
                  rel="noopener noreferrer"
                  >Discord</a
                ></span
              >
            </li>
          </ul>
        </div>

        <div id="contact" class="scroll-mt-24" aria-labelledby="contact-heading">
          <h2 id="contact-heading" class="font-display text-2xl font-bold tracking-tight">
            Contact
          </h2>
          <p class="mt-2 text-sm text-[var(--text-muted)]">
            Partnerships, press, support, or just saying hi — we read every message.
          </p>
          <div class="contact-panel mt-6">
            <ContactForm />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { byCategory, visibleStoreLinks, SECTION_META, catalogPath } from '@/data/products'
import { imageForProduct } from '@/data/productImages'
import ContactForm from '@/components/ContactForm.vue'

const sections = computed(() =>
  ['games', 'extensions', 'apps'].map((key) => ({
    ...SECTION_META[key],
    items: byCategory(key).map((p) => ({
      ...p,
      image: imageForProduct(p.id),
    })),
  })),
)

const counts = computed(() => ({
  games: byCategory('games').length,
  apps: byCategory('apps').length,
  extensions: byCategory('extensions').length,
}))

const featured = computed(() => {
  const ids = ['daily-quiz', 'crystal-blast-3d', 'ink-surge', 'magekeep-siege', 'shoot']
  return ids
    .map((id) => {
      const section = sections.value.flatMap((s) => s.items).find((p) => p.id === id)
      if (!section) return null
      return {
        ...section,
        blurb:
          section.category === 'games'
            ? 'Play free in browser'
            : section.category === 'apps'
              ? 'App Store & more'
              : 'Chrome extension',
      }
    })
    .filter(Boolean)
    .slice(0, 4)
})

function linksFor(product) {
  return visibleStoreLinks(product.storeLinks)
}

function isExternal(url) {
  return /^https?:\/\//i.test(url)
}

const PLATFORM_LABELS = {
  app_store: 'App Store',
  google_play: 'Google Play',
  amazon: 'Amazon',
  chrome_web_store: 'Chrome Web Store',
  web: 'Play now',
}

function platformLabel(platform) {
  return PLATFORM_LABELS[platform] || platform
}
</script>

<style scoped>
.stat {
  border-radius: 0.85rem;
  border: 1px solid var(--surface-border);
  background: rgba(255, 255, 255, 0.02);
  padding: 0.75rem 0.85rem;
}

.featured-panel {
  border-radius: 1.25rem;
  border: 1px solid var(--surface-border);
  background:
    linear-gradient(160deg, rgba(255, 99, 130, 0.08), transparent 40%),
    var(--surface);
  padding: 1.15rem 1.15rem 1.25rem;
  box-shadow: var(--shadow-card);
}

.featured-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  border-radius: 0.85rem;
  border: 1px solid transparent;
  background: rgba(0, 0, 0, 0.22);
  padding: 0.55rem 0.7rem;
  text-decoration: none;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;
}

.featured-row:hover {
  border-color: color-mix(in srgb, var(--accent) 35%, transparent);
  background: rgba(0, 0, 0, 0.35);
}

.product-card {
  overflow: hidden;
  border-radius: 1.1rem;
  border: 1px solid var(--surface-border);
  background: var(--surface);
  box-shadow: var(--shadow-card);
  transition:
    border-color 0.2s ease,
    transform 0.2s ease,
    background 0.2s ease;
}

.product-card:hover {
  border-color: var(--surface-border-strong);
  background: var(--surface-hover);
  transform: translateY(-2px);
}

.product-art {
  position: relative;
  isolation: isolate;
  background:
    radial-gradient(120% 90% at 50% 18%, rgba(255, 99, 130, 0.14), transparent 55%),
    radial-gradient(90% 80% at 80% 90%, rgba(120, 90, 255, 0.1), transparent 50%),
    linear-gradient(160deg, #1e1e2a 0%, #12121a 48%, #0c0c12 100%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    inset 0 0 0 1px rgba(255, 255, 255, 0.04);
}

.product-art::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.05) 0%,
    transparent 35%,
    rgba(0, 0, 0, 0.18) 100%
  );
  z-index: 1;
}

.product-art img {
  position: relative;
  z-index: 0;
  display: block;
  background: transparent;
}

.contact-panel {
  border-radius: 1.15rem;
  border: 1px solid var(--surface-border);
  background: var(--surface);
  padding: 1.25rem;
  box-shadow: var(--shadow-card);
}

@media (min-width: 640px) {
  .contact-panel {
    padding: 1.5rem;
  }
}
</style>
